<template>
  <div
    class="home"
    v-loading.fullscreen.lock="isLoading"
  >
    <!-- ステータスタブと新規注文作成 -->
    <HomeHeader
      :status.sync="status"
      :orderCountByStatus="orderCountByStatus"
      @keepHomeState="keepHomeState"
      @onChangeStatus="onChangeStatus"
    />

    <!-- 件数とフィルタ -->
    <HomeSub
      :total="total"
      :current-page="currentPage"
      :page-size="pageSize"
      :is-show-filter-modal.sync="isShowFilterModal"
      :is-exporting="isExporting"
      :is-filtered="isFiltered"
      :is-only-orders-in-charge.sync="isOnlyOrdersInCharge"
      @onExportOrders="onExportOrders"
      @onClearFilterConditions="onClearFilterConditions"
      @onChangeStatus="onChangeStatus"
    >
      <HomeFilter
        ref="filter"
        slot="filterModal"
        :status="status"
        :is-show-filter-modal.sync="isShowFilterModal"
        :is-filtered="isFiltered"
        @onChangeStatus="onChangeStatus"
        @onResetFilterConditions="onResetFilterConditions"
        @onChangeFilterConditions="onChangeFilterConditions"
      />
    </HomeSub>

    <!-- 注文一覧 -->
    <HomeOrders
      ref="homeOrders"
      :status="status"
      :orders="orders"
      :sortParams="sortParams"
      @keepHomeState="keepHomeState"
      @updateSortDirection="updateSortDirection"
    />

    <!-- ページャ -->
    <HomePagination
      :total="total"
      :total-page="totalPage"
      :page-size="pageSize"
      :current-page.sync="currentPage"
      @onChangeCurrentPage="onChangeCurrentPage"
    />
  </div>
</template>

<script>
import _ from "lodash";
import OrderUsecase from "@/usecases/order.usecase";
import { apiErrorHandler } from "@/services/apiErrorHandler";
import HomeHeader from "@/components/Home/Header.vue";
import HomeSub from "@/components/Home/Sub.vue";
import HomeFilter from "@/components/Home/Filter.vue";
import HomeOrders from "@/components/Home/Orders.vue";
import HomePagination from "@/components/Home/Pagination.vue";

const PAGE_SIZE = 50;
const BASE_FILTER_CONDITIONS = {
  status: null,
};
const NOTIFY_MESSAGE_UNAVAILABLE_USER = {
  title: "エラーが発生しました",
  message: "有効なユーザではありません",
};
const NOTIFY_MESSAGE_EXPORTED = {
  title: "CSVへの出力を行いました",
};

const ORDER_STATUSES = [
  "DRAFT",
  "ORDER_REQUEST",
  "PROCESSING",
  "PROCESSING_CREATING_LAYOUT",
  "REVISING_LAYOUT",
  "CONFIRMED",
  // "SHIPPED",
];

export default {
  components: {
    HomeHeader,
    HomeSub,
    HomeFilter,
    HomeOrders,
    HomePagination,
  },
  data () {
    return {
      status: "ALL",
      total: 0,
      totalPage: 1,
      pageSize: PAGE_SIZE,
      currentPage: 1,
      isLoading: false,
      isFiltered: false,
      isExporting: false,
      isShowFilterModal: false,
      filterConditions: _.cloneDeep(BASE_FILTER_CONDITIONS),
      orders: [],
      position: 0,
      sortParams: {
        sortKey: "orderedAt",
        sortDirection: "DESC",
      },
      orderCountByStatus: {},
      isOnlyOrdersInCharge: false,
    };
  },
  computed: {
    currentUser () {
      return this.$store.state.user;
    },
  },
  created () {
    this.restoreHomeState();
  },
  mounted () {
    this.fetchOrders();
  },
  methods: {
    /**
     * [restoreHomeState description]
     */
    restoreHomeState () {
      const homeState = sessionStorage.getItem("homeState");
      const { role } = this.currentUser;

      if (homeState) {
        const values = JSON.parse(homeState);
        const {
          status,
          currentPage,
          filterConditions,
          isFiltered,
          position,
          isOnlyOrdersInCharge,
          isCreateOrder,
        } = values;

        if (!isCreateOrder) {
          this.status = status;
          this.currentPage = currentPage;
          this.filterConditions = filterConditions;
          this.filterConditions.status = status === "ALL" ? undefined : status;
          this.isFiltered = isFiltered;
          this.position = position;
        }
        this.isOnlyOrdersInCharge = isOnlyOrdersInCharge || false;

        sessionStorage.removeItem("homeState");
      } else {
        this.isOnlyOrdersInCharge = role === "CLIENT_CONTRIBUTOR";
      }
    },

    /**
     * [keepHomeState description]
     * @param   {[type]}  position  [position description]
     */
    keepHomeState (position, isCreateOrder = false) {
      const {
        status,
        currentPage,
        filterConditions,
        isFiltered,
        isOnlyOrdersInCharge,
      } = this;
      const values = {
        status,
        currentPage,
        filterConditions,
        isFiltered,
        position,
        isOnlyOrdersInCharge,
        isCreateOrder,
      };

      sessionStorage.setItem("homeState", JSON.stringify(values));
    },

    /**
     * [checkUser description]
     * @return  {[type]}  [return description]
     */
    checkUser () {
      const user = this.$store.state.user;

      return !user || !user.sub;
    },

    /**
     * [fetchOrders description]
     */
    async fetchOrders () {
      const isUnavailableUser = this.checkUser();

      if (isUnavailableUser) {
        this.$notify.error(NOTIFY_MESSAGE_UNAVAILABLE_USER);
        return;
      }

      // 読み込み中にする
      this.isLoading = true;

      try {
        const payload = {
          q: this.filterConditions,
          page: this.currentPage,
          pageSize: PAGE_SIZE,
          sortKey: this.sortParams.sortKey,
          sortDirection: this.sortParams.sortDirection,
          mineFlg: this.isOnlyOrdersInCharge,
        };

        // 実行
        const res = await orderUsecase.getOrders(payload);
        this.getOrderByStatusFilter();

        // 注文一覧を更新
        this.update(res);
      } catch (err) {
        const onError = () => {};
        apiErrorHandler(this, err, onError);
      } finally {
        // 読み込み終了
        this.isLoading = false;

        _.defer(() => {
          this.$refs.homeOrders.setPosition(this.position);
          this.position = 0;
        });
      }
    },

    /**
     * function that get Order counts by status
     * @returns {any}
     */
    async getOrderByStatusFilter () {
      const statuses = ORDER_STATUSES;
      const promises = [];
      statuses.forEach((status) => {
        const payload = {
          q: { ...this.filterConditions, status: status === "ALL" ? undefined : status },
          status,
        };
        promises.push(orderUsecase.getOrderCount(payload));
      });
      try {
        const result = await Promise.all(promises);
        result.forEach((count, index) => {
          this.$set(this.orderCountByStatus, statuses[index], count);
        });
      } catch (e) {
        const onError = () => {};
        apiErrorHandler(this, e, onError);
      }
    },

    /**
     * [update description]
     * @param   {[type]}  res  [res description]
     */
    update (res) {
      this.total = res.total;
      this.totalPage = res.totalPage;
      this.orders = res.orders;
    },

    /**
     * [onChangeStatus description]
     */
    onChangeStatus () {
      const { status, filterConditions } = this;

      filterConditions.status = status === "ALL" ? undefined : status;
      this.currentPage = 1;

      this.$nextTick(() => {
        this.fetchOrders();
      });
    },

    /**
     * [onChangeCurrentPage description]
     */
    onChangeCurrentPage () {
      this.$nextTick(() => {
        this.fetchOrders();
      });
    },

    /**
     * [onChangeFilterCondition description]
     */
    onChangeFilterConditions (filterConditions) {
      this.isFiltered = true;
      this.filterConditions = filterConditions;

      this.$nextTick(() => {
        this.onChangeStatus();
      });
    },

    /**
     * [onResetFilterConditions description]
     */
    onResetFilterConditions () {
      this.isFiltered = false;
      this.filterConditions = _.cloneDeep(BASE_FILTER_CONDITIONS);
    },

    /**
     * [onClearFilterConditions description]
     */
    onClearFilterConditions () {
      // 条件をクリア時に HomeFilter の検索条件もリセットする
      // 以下で onResetFilterConditions も走る
      this.$refs.filter.reset(true);

      this.$nextTick(() => {
        this.onChangeStatus();
      });
    },

    /**
     * [onExportOrders description]
     */
    async onExportOrders () {
      const isUnavailableUser = this.checkUser();

      if (isUnavailableUser) {
        this.$notify.error(NOTIFY_MESSAGE_UNAVAILABLE_USER);
        return;
      }

      this.isExporting = true;

      try {
        const payload = {
          q: this.filterConditions,
        };

        const { url } = await orderUsecase.exportOrders(payload);

        this.$notify.success(NOTIFY_MESSAGE_EXPORTED);
        this.downloadCSV(url);
      } catch (err) {
        const onError = () => {};
        apiErrorHandler(this, err, onError);
      } finally {
        this.isExporting = false;
      }
    },

    /**
     * [downloadCSV description]
     * @param   {[type]}  url  [url description]
     */
    downloadCSV (url) {
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "orders.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    /**
     * [updateSortDirection description]
     * @param   {[type]}  sortParams  [sortParams description]
     */
    updateSortDirection (sortParams) {
      this.sortParams.sortKey = sortParams.sortKey; // || "orderedAt";
      this.sortParams.sortDirection = sortParams.sortDirection; // || "DESC";
      this.currentPage = 1;
      this.$nextTick(() => {
        this.fetchOrders();
      });
    },
  },
};

const orderUsecase = new OrderUsecase();
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

.home {
  display: grid;
  height: calc(100% - 63px);
  grid-template-rows: auto auto 1fr auto;
  grid-template-columns: 1fr;
}

.page-info {
  padding: 0px 18px 12px;
}
</style>
